import { useState, useEffect } from 'react'
import { readStats, Datapoint } from '../service/stats_reader'
import { DailyTraffic, ChapterStats, AudiobookChapter } from '../types/stats'

const chapters: AudiobookChapter[] = [
  { id: 1, title: 'Rozdział 1: Smok' },
  { id: 2, title: 'Rozdział 2: Legenda' },
  { id: 3, title: 'Rozdział 3: Znikające kanapki' },
  { id: 4, title: 'Rozdział 4: Bocian' },
  { id: 5, title: 'Rozdział 5: Klucz' },
  { id: 6, title: 'Rozdział 6: Zamek' },
  { id: 7, title: 'Rozdział 7: Złota waga' },
  { id: 8, title: 'Rozdział 8: Sens życia' }
]

const formatDatePolish = (dateStr: string): string => {
  const months = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru']
  const date = new Date(dateStr)
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
}

const transformToDailyTraffic = (datapoints: Datapoint[]): DailyTraffic[] => {
  const countsByDate = new Map<string, number>()

  datapoints.forEach(dp => {
    const dateOnly = dp.updatedAt.split(' ')[0]
    countsByDate.set(dateOnly, (countsByDate.get(dateOnly) || 0) + 1)
  })

  return Array.from(countsByDate.entries())
    .map(([date, count]) => ({
      date,
      displayDate: formatDatePolish(date),
      count
    }))
    .sort((a, b) => a.date.localeCompare(b.date))
}

const transformToChapterStats = (datapoints: Datapoint[]): ChapterStats[] => {
  const countsByChapter = new Map<number, number>()

  datapoints.forEach(dp => {
    countsByChapter.set(dp.chapterId, (countsByChapter.get(dp.chapterId) || 0) + 1)
  })

  return chapters
    .map(chapter => ({
      chapterId: chapter.id,
      chapterName: chapter.title,
      totalPlays: countsByChapter.get(chapter.id) || 0
    }))
    .sort((a, b) => b.totalPlays - a.totalPlays)
}

interface UseStatsResult {
  dailyTraffic: DailyTraffic[]
  chapterStats: ChapterStats[]
  rawData: Datapoint[]
  isLoading: boolean
  error: string | null
}

export const useStats = (): UseStatsResult => {
  const [dailyTraffic, setDailyTraffic] = useState<DailyTraffic[]>([])
  const [chapterStats, setChapterStats] = useState<ChapterStats[]>([])
  const [rawData, setRawData] = useState<Datapoint[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const data = await readStats()

        if (data.length === 0) {
          setError('Brak danych do wyświetlenia')
        } else {
          setRawData(data)
          setDailyTraffic(transformToDailyTraffic(data))
          setChapterStats(transformToChapterStats(data))
        }
      } catch (err) {
        setError('Wystąpił błąd podczas pobierania danych')
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchStats()
  }, [])

  return { dailyTraffic, chapterStats, rawData, isLoading, error }
}
